import React from "react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { describeHandoff, isDifferentOwner } from "../utils/sessionHandoff";

/**
 * "You are about to continue someone's estimate."
 *
 * A browser profile's session is shared by every tab and by everyone who uses
 * the machine, so opening CostPilot can silently drop an estimator inside the
 * last person's job. This asks first: it names the estimate and who left it,
 * and nothing is loaded until Resume is pressed. Start fresh forgets the
 * marker and hands over to the normal new-estimate flow.
 */
function SessionResumeDialog({
  open,
  marker,
  currentEmail = null,
  isResuming = false,
  onResume,
  onStartFresh,
}) {
  if (!marker) return null;

  // Continuing here would save into someone else's estimate under this login.
  // That is the one case worth interrupting for, so it reads as a warning
  // rather than as another line of description.
  const differentOwner = isDifferentOwner(marker, currentEmail);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        // Dismissing is "not this estimate" — never a silent resume.
        if (!nextOpen && !isResuming) onStartFresh();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Resume this estimate?</DialogTitle>
          <DialogDescription>
            {describeHandoff(marker, currentEmail)} Nothing is loaded until you
            choose.
          </DialogDescription>
        </DialogHeader>
        <p style={{ fontSize: "1rem", fontWeight: 650, margin: "0.25rem 0" }}>
          {marker.jobName}
        </p>
        {differentOwner ? (
          <p role="alert" className="session-resume__warning">
            This estimate belongs to {marker.userEmail}. Resuming saves into it
            as {currentEmail}.
          </p>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onStartFresh}
            disabled={isResuming}
          >
            Start fresh
          </Button>
          <Button type="button" onClick={onResume} disabled={isResuming}>
            {isResuming ? "Opening…" : "Resume this estimate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SessionResumeDialog;
